
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import Page from "@/layout/Page.vue";
import { HomeModule } from "@/store/modules/HomeModule";
import { QuillEditor } from "@vueup/vue-quill";
import type { UploadFile } from "element-plus";
import { ElMessage, UploadProps } from "element-plus";
import { defineComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

interface FaqItem {
  pregunta: string;
  respuesta: string;
}

export default defineComponent({
  name: "home-editor",
  components: { Page, QuillEditor },
  setup() {
    const store = useStore();
    const homeModule = getModule(HomeModule, store);
    const { t } = useI18n();

    const guardando = ref(false);
    const headerImageUrl = ref("");
    const introHtml = ref("");
    const faqsCursos = ref<FaqItem[]>([{ pregunta: "", respuesta: "" }]);
    const faqsIam = ref<FaqItem[]>([{ pregunta: "", respuesta: "" }]);

    // Referencias para las URLs finales que guardaremos
    const pdfResumenUrl = ref("");
    const pdfProgramaUrl = ref("");
    const pdfResumenName = ref("");
    const pdfProgramaName = ref("");

    // Referencias para almacenar los archivos en memoria del navegador antes de guardar
    const resumenFile = ref<File | null>(null);
    const programaFile = ref<File | null>(null);

    onMounted(async () => {
      await homeModule.fetchConfig();

      setCurrentPageBreadcrumbs(t("menu.home"), [
        {
          title: t("menu.designContenido"),
          path: "/website/inicio",
        },
      ]);

      headerImageUrl.value =
        homeModule.headerImage || "media/logos/logo_iam.png";
      introHtml.value = homeModule.introHtml;
      pdfResumenUrl.value = homeModule.pdfResumen || "";
      pdfResumenName.value = homeModule.pdfResumenName || "";
      pdfProgramaUrl.value = homeModule.pdfPrograma || "";
      pdfProgramaName.value = homeModule.pdfProgramaName || "";

      faqsCursos.value =
        homeModule.faqsCursos.length > 0
          ? [...homeModule.faqsCursos]
          : [{ pregunta: "", respuesta: "" }];
      faqsIam.value =
        homeModule.faqsIam.length > 0
          ? [...homeModule.faqsIam]
          : [{ pregunta: "", respuesta: "" }];
    });

    // Función de validación de imagen (Igual que en CursoDetalle)
    const beforeHeaderUpload: UploadProps["beforeUpload"] = (rawFile) => {
      // 1. Validar formato
      if (
        rawFile.type !== "image/jpeg" &&
        rawFile.type !== "image/png" &&
        rawFile.type !== "image/gif"
      ) {
        ElMessage.error(t("imagen.errorFormat"));
        return false;
      }

      // 2. Validar tamaño (2MB)
      if (rawFile.size / 1024 / 1024 > 2) {
        ElMessage.error(t("imagen.errorSize"));
        return false;
      }

      // 3. Procesar archivo a Base64
      const reader = new FileReader();
      reader.readAsDataURL(rawFile);
      reader.onload = function () {
        headerImageUrl.value = reader.result as string;
      };
      reader.onerror = function (error) {
        ElMessage.error("Error: " + error);
      };

      return false;
    };

    const onResumenChange = (uploadFile: UploadFile) => {
      const rawFile = uploadFile.raw;
      if (!rawFile) return;

      if (rawFile.type !== "application/pdf") {
        ElMessage.error(t("pdf.errorFormat"));
        return;
      }

      if (rawFile.size / 1024 / 1024 > 5) {
        ElMessage.error(t("pdf.errorSize"));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(rawFile);
      reader.onload = () => {
        pdfResumenUrl.value = reader.result as string; // Base64
        pdfResumenName.value = rawFile.name; // Nombre original ("ficha.pdf")
      };
      reader.onerror = (error) => {
        ElMessage.error("Error al procesar el archivo: " + error);
      };
    };

    // Cambio del archivo PDF Programa (Uso de funciones flecha)
    const onProgramaChange = (uploadFile: UploadFile) => {
      const rawFile = uploadFile.raw;
      if (!rawFile) return;

      if (rawFile.type !== "application/pdf") {
        ElMessage.error(t("pdf.errorFormat"));
        return;
      }

      if (rawFile.size / 1024 / 1024 > 5) {
        ElMessage.error(t("pdf.errorSize"));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(rawFile);
      reader.onload = () => {
        pdfProgramaUrl.value = reader.result as string; // Base64
        pdfProgramaName.value = rawFile.name; // Nombre original ("programa.pdf")
      };
      reader.onerror = (error) => {
        ElMessage.error("Error al procesar el archivo: " + error);
      };
    };

    const removePdf = (type: "resumen" | "programa") => {
      if (type === "resumen") {
        pdfResumenUrl.value = "";
        pdfResumenName.value = "";
        resumenFile.value = null;
      } else {
        pdfProgramaUrl.value = "";
        pdfProgramaName.value = "";
        programaFile.value = null;
      }
    };

    const openPdf = (base64String: string, filename: string) => {
      if (!base64String) return;

      // Si el archivo se acaba de seleccionar localmente, ya es una URL de tipo 'blob:' y se puede abrir directamente
      if (base64String.startsWith("blob:")) {
        window.open(base64String, "_blank");
        return;
      }

      try {
        // Extraemos la parte del Base64 y el tipo de contenido
        const parts = base64String.split(";base64,");
        const contentType = parts[0].split(":")[1];
        const raw = window.atob(parts[1]);
        const rawLength = raw.length;
        const uInt8Array = new Uint8Array(rawLength);

        // Convertimos los caracteres de la cadena a bytes
        for (let i = 0; i < rawLength; ++i) {
          uInt8Array[i] = raw.charCodeAt(i);
        }

        // Creamos un Blob binario y generamos una URL segura que el navegador sí permite abrir
        const blob = new Blob([uInt8Array], { type: contentType });
        const blobUrl = URL.createObjectURL(blob);

        window.open(blobUrl, "_blank");
      } catch (error) {
        ElMessage.error("Error al abrir el archivo PDF");
        console.error(error);
      }
    };

    const addItem = (type: "cursos" | "iam") => {
      const target = type === "cursos" ? faqsCursos : faqsIam;
      target.value.push({ pregunta: "", respuesta: "" });
    };

    const removeItem = (index: number, type: "cursos" | "iam") => {
      const target = type === "cursos" ? faqsCursos : faqsIam;
      target.value.splice(index, 1);
    };

    const move = (index: number, dir: number, type: "cursos" | "iam") => {
      const target = type === "cursos" ? faqsCursos : faqsIam;
      const newIdx = index + dir;
      const temp = target.value[index];
      target.value[index] = target.value[newIdx];
      target.value[newIdx] = temp;
    };

    const handleSave = async () => {
      guardando.value = true;

      // Empaquetamos en JSON el contenido Base64 junto al nombre original
      const valueResumen = pdfResumenUrl.value
        ? JSON.stringify({
            url: pdfResumenUrl.value,
            name: pdfResumenName.value,
          })
        : "";

      const valuePrograma = pdfProgramaUrl.value
        ? JSON.stringify({
            url: pdfProgramaUrl.value,
            name: pdfProgramaName.value,
          })
        : "";

      const payload = [
        { key: "home_header_image", value: headerImageUrl.value },
        { key: "home_intro_html", value: introHtml.value },
        { key: "home_faqs_cursos", value: JSON.stringify(faqsCursos.value) },
        { key: "home_faqs_iam", value: JSON.stringify(faqsIam.value) },
        { key: "home_pdf_resumen", value: valueResumen },
        { key: "home_pdf_programa", value: valuePrograma },
      ];

      try {
        await homeModule.updateConfig(payload);
        ElMessage.success(t("page.website.home.guardarExito"));
      } catch (err) {
        ElMessage.error(t("page.website.home.guardarError"));
      } finally {
        guardando.value = false;
      }
    };

    return {
      headerImageUrl,
      introHtml,
      pdfResumenUrl,
      pdfProgramaUrl,
      pdfResumenName,
      pdfProgramaName,
      resumenFile,
      programaFile,
      faqsCursos,
      faqsIam,
      guardando,
      beforeHeaderUpload,
      onResumenChange,
      onProgramaChange,
      removePdf,
      openPdf,
      addItem,
      removeItem,
      move,
      handleSave,
    };
  },
});
